/* eslint-disable no-nested-ternary */
import AppDispacher from "./actions";
import MetaConfigTags from "./components/MetaConfig";
import Scripts from "./components/Scripts";
import ChinaScripts from "./components/ChinaScripts";
import BlogScripts from "./components/BlogScripts";
import LoginWrapper from "@Components/Login";
import ToastNotification from "@Components/ToastNotification";
import UcasAnnouncementModal from "@Components/UcasAnnouncementModal";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import getVisitorId from "helpers/getVisitorId";
import isInternalUser from "@Utils/isInternalUser";
import { AppState } from "@Store/index";
import config from "@Config/index";
import useWhitelabel from "hooks/useWhitelabel";
import WhitelabelScripts from "./components/WhitelabelScripts";
import useIsMiniProgram from "hooks/useIsMiniProgram";
import MiniProgramScripts from "./components/MiniProgramScripts";
import AppScripts from "./components/Scripts/AppScripts";
import useIsApp from "hooks/useIsApp";
import { registerPostHogSuperProperties, setDestinationCountryForPostHog } from "@Utils/posthog";

const GlobalComponentsWrapper = ({ growthbook }: any) => {
  const dispatch = useDispatch();
  const {
    userData,
    locality,
    isDesktop,
    countryCode,
    regionId,
    nationality,
    providerId,
    country,
    inventoryId,
    nonce,
    moduleName,
  } = useSelector(({ appData }: AppState) => appData);

  const { isWhitelabel } = useWhitelabel();
  const isMiniProgram = useIsMiniProgram();
  const isApp = useIsApp();

  // Check if current page is a content page (blog/news) based on moduleName
  const isContentPage = useMemo(
    () => moduleName?.startsWith("blog_") || moduleName?.startsWith("news_") || false,
    [moduleName],
  );

  useEffect(() => {
    if (!config.IS_CHINA_APP) {
      growthbook.init();
    }
    const appDispatcher = new AppDispacher(dispatch);
    appDispatcher.fetchCountryData();
    appDispatcher.fetchGlobalTrustPilotRating();
  }, []);

  useEffect(() => {
    if (!countryCode) return;
    setDestinationCountryForPostHog(countryCode);
    registerPostHogSuperProperties().catch(() => undefined);
  }, [countryCode]);

  useEffect(() => {
    if (!config.IS_CHINA_APP) {
      growthbook.setAttributes({
        id: getVisitorId(),
        internal_user: isInternalUser(userData),
        ...(userData?.data?.email ? { email: userData?.data?.email } : {}),
        ...(locality ? { locality } : {}),
        ...(country ? { country } : {}),
        ...(countryCode ? { countryCode } : {}),
        ...(regionId ? { regionId } : {}),
        ...(providerId ? { providerId } : {}),
        ...(nationality ? { nationality } : {}),
        ...(inventoryId ? { inventoryId } : {}),
        deviceType: isDesktop ? "desktop" : "mobile",
        isWhitelabel: isWhitelabel ? "true" : "false",
      });
    }
  }, [
    locality,
    countryCode,
    isWhitelabel,
    country,
    JSON.stringify(userData),
    regionId,
    providerId,
    nationality,
  ]);

  const getScriptComponent = () => {
    if (__CLIENT__ || __DEV__) return null;

    switch (true) {
      case isApp:
        return <AppScripts nonce={nonce} />;
      case isMiniProgram:
        return <MiniProgramScripts />;
      case config.IS_CHINA_APP:
        return <ChinaScripts nonce={nonce} />;
      case config.IS_WHITELABEL_APP:
        return <WhitelabelScripts nonce={nonce} />;
      case isContentPage:
        return <BlogScripts nonce={nonce} />;
      default:
        return <Scripts nonce={nonce} />;
    }
  };

  return (
    <>
      <LoginWrapper />
      <MetaConfigTags />
      <ToastNotification />
      <UcasAnnouncementModal />
      {getScriptComponent()}
    </>
  );
};

export default GlobalComponentsWrapper;
